// ============================================================
// Square Code — client-starter entry
//
// Boot order:
//   1. Inject import.meta.env into @square/core via setFrontendEnv
//      — Vite statically replaces `import.meta.env.*` in THIS file
//      at build time, so the bag we pass here is fully materialized.
//      No shared-package code ever reads import.meta.env directly.
//   2. Init Sentry (if VITE_SENTRY_DSN set)
//   3. Resolve the active vertical
//   4. Mount the app
//
// The shell renders regardless of whether Supabase env is present.
// When Supabase is unavailable the four shell hooks fall back to
// honest demo mode and the mode pill reads DEMO.
// ============================================================

import React from 'react'
import ReactDOM from 'react-dom/client'
import {
  setFrontendEnv,
  loadFrontendEnv,
  getVertical,
  DEFAULT_VERTICAL,
  type VerticalConfig,
} from '@square/core'
import '@square/ui/tokens.css'
import '@square/ui/sq-v2-surface.css'

import { SquareApp } from '@square/shell'

import { initInstrumentation } from './instrument'

// 1. Inject the Vite-resolved env bag into @square/core BEFORE any
//    other import path can read it. Vite replaces `import.meta.env`
//    with a static object at build time here.
setFrontendEnv(import.meta.env as unknown as Record<string, unknown>)

async function boot() {
  // 2. Sentry — optional. Safe no-op when DSN missing.
  await initInstrumentation()

  const env = loadFrontendEnv()

  // Honest warning when Supabase isn't wired. Not fatal — the shell
  // runs in demo mode and every hook handles this cleanly.
  if (!env.VITE_SUPABASE_URL || !env.VITE_SUPABASE_ANON_KEY) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Square Code] Supabase env missing — running in demo mode. ' +
      'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to enable live mode.'
    )
  }

  // 3. Vertical resolution — defaults to the canonical Square Code shell.
  const requestedId = (import.meta.env.VITE_SQUARE_VERTICAL as string | undefined) || DEFAULT_VERTICAL.id
  const vertical: VerticalConfig = getVertical(requestedId) ?? DEFAULT_VERTICAL
  if (vertical.id !== requestedId) {
    // eslint-disable-next-line no-console
    console.warn(`[client-starter] Unknown vertical "${requestedId}", falling back to "${vertical.id}"`)
  }

  // 4. Mount.
  const root = document.getElementById('root')
  if (!root) throw new Error('[client-starter] #root element missing from index.html')

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <SquareApp vertical={vertical} env={env} />
    </React.StrictMode>
  )
}

boot().catch((err) => {
  // eslint-disable-next-line no-console
  console.error('[client-starter] boot failed', err)
  // Last-resort fallback so the page is never entirely blank.
  const root = document.getElementById('root')
  if (root) {
    root.innerHTML = `
      <div style="
        max-width: 560px; margin: 80px auto; padding: 24px;
        background: rgba(255,82,82,0.06);
        border: 1px solid rgba(255,82,82,0.28);
        border-radius: 14px;
        font-family: system-ui, sans-serif;
        color: #edf2ff;
      ">
        <div style="font-size:11px; color:#ff9f9f; letter-spacing:0.14em; margin-bottom:10px; font-weight: 700;">
          SQUARE CODE · BOOT FAILURE
        </div>
        <pre style="font-size:12px; white-space:pre-wrap; word-break:break-word;">${String((err as Error)?.message || err)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')}</pre>
      </div>
    `
  }
})
